import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Key, Copy, Trash2, CheckCircle2, Clock, XCircle, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  active: {
    label_ar: 'نشط',
    label_en: 'Active',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/15 border-emerald-500/40',
    Icon: Clock,
  },
  used: {
    label_ar: 'مستخدم',
    label_en: 'Used',
    color: 'text-blue-400',
    bg: 'bg-blue-500/15 border-blue-500/40',
    Icon: CheckCircle2,
  },
  expired: {
    label_ar: 'منتهي',
    label_en: 'Expired',
    color: 'text-gray-400',
    bg: 'bg-gray-500/15 border-gray-500/40',
    Icon: XCircle,
  },
  revoked: {
    label_ar: 'ملغي',
    label_en: 'Revoked',
    color: 'text-red-400',
    bg: 'bg-red-500/15 border-red-500/40',
    Icon: XCircle,
  },
};

const OverrideCodesTab = ({ isArabic }) => {
  const [data, setData] = useState({ items: [], total: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [minutes, setMinutes] = useState(60);
  const [note, setNote] = useState('');
  const [lastCode, setLastCode] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      const res = await api.get(`/loyalty/override-codes?${params.toString()}`);
      setData(res.data || { items: [], total: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل تحميل الرموز' : 'Failed to load codes'));
    } finally {
      setLoading(false);
    }
  }, [filter, isArabic]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/loyalty/override-codes', {
        purpose: 'attach_old_membership',
        expires_in_minutes: Number(minutes) || 60,
        note: note.trim() || null,
      });
      setLastCode(res.data);
      setNote('');
      toast.success(isArabic ? `تم توليد الرمز: ${res.data.code}` : `Code generated: ${res.data.code}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل التوليد' : 'Failed'));
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(isArabic ? 'تم النسخ' : 'Copied');
    } catch {
      toast.error(isArabic ? 'فشل النسخ' : 'Failed to copy');
    }
  };

  const handleRevoke = async (id, code) => {
    if (!window.confirm(isArabic ? `إلغاء الرمز ${code}؟` : `Revoke code ${code}?`)) return;
    try {
      await api.delete(`/loyalty/override-codes/${id}`);
      toast.success(isArabic ? 'تم الإلغاء' : 'Revoked');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل الإلغاء' : 'Failed'));
    }
  };

  const remainingMinutes = (expiresAt) => {
    try {
      const ms = new Date(expiresAt).getTime() - Date.now();
      const m = Math.max(0, Math.floor(ms / 60000));
      return m;
    } catch {
      return 0;
    }
  };

  return (
    <Card className="overflow-hidden bg-card border-white/10" data-testid="override-codes-panel">
      <div className="p-4 border-b border-white/10">
        <h3 className="font-bold flex items-center gap-2">
          <Key className="w-4 h-4 text-amber-400" />
          {isArabic ? 'رموز الإذن لمرة واحدة' : 'One-time Override Codes'}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          {isArabic
            ? 'يولِّد المدير رمزاً يستخدمه الحلاق لمرة واحدة لتجاوز حد الـ 3 أيام عند ربط إيراد سابق ببطاقة قديمة.'
            : 'Admin generates a one-time code allowing a barber to bypass the 3-day limit when linking a past revenue to an older membership.'}
        </p>
      </div>

      {/* Generator */}
      <div className="p-4 border-b border-white/10 bg-secondary/20 space-y-3">
        <h4 className="text-sm font-semibold">{isArabic ? 'توليد رمز جديد' : 'Generate new code'}</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              {isArabic ? 'مدة الصلاحية (دقيقة)' : 'Validity (minutes)'}
            </label>
            <Input
              type="number"
              min="5"
              max="1440"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              data-testid="override-minutes-input"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="text-xs text-muted-foreground mb-1 block">
              {isArabic ? 'ملاحظة (اختياري)' : 'Note (optional)'}
            </label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isArabic ? 'سبب التوليد...' : 'Reason...'}
              data-testid="override-note-input"
            />
          </div>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            data-testid="override-generate-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Key className="w-4 h-4 me-2" />}
            {isArabic ? 'توليد الرمز' : 'Generate'}
          </Button>
        </div>

        {lastCode && (
          <div className="mt-3 p-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="text-xs text-muted-foreground mb-1">
                {isArabic ? 'الرمز الجديد (شاركه مع الحلاق)' : 'New code (share with the barber)'}
              </div>
              <div className="text-3xl font-mono font-bold tracking-widest text-emerald-300" data-testid="last-generated-code">
                {lastCode.code}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {isArabic ? 'ساري لـ' : 'Valid for'} {lastCode.expires_in_minutes} {isArabic ? 'دقيقة' : 'min'}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => handleCopy(lastCode.code)} data-testid="copy-last-code-btn">
              <Copy className="w-4 h-4 me-1" />
              {isArabic ? 'نسخ' : 'Copy'}
            </Button>
          </div>
        )}
      </div>

      {/* Filter */}
      <div className="p-3 border-b border-white/10 flex gap-2 flex-wrap">
        {['all', 'active', 'used', 'expired', 'revoked'].map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? 'default' : 'outline'}
            onClick={() => setFilter(f)}
            data-testid={`override-filter-${f}`}
          >
            {f === 'all'
              ? isArabic ? 'الكل' : 'All'
              : isArabic ? STATUS_CONFIG[f].label_ar : STATUS_CONFIG[f].label_en}
          </Button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-10 text-muted-foreground">
          {isArabic ? 'جاري التحميل...' : 'Loading...'}
        </div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          {isArabic ? '✅ لا توجد رموز' : '✅ No codes'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="override-codes-table">
            <thead className="bg-secondary/40">
              <tr>
                <th className="text-start py-3 px-3">{isArabic ? 'الرمز' : 'Code'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'الحالة' : 'Status'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'المنشئ' : 'Created by'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'تاريخ الإنشاء' : 'Created at'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'الصلاحية المتبقية' : 'Remaining'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'مستخدم بواسطة' : 'Used by'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'ملاحظة' : 'Note'}</th>
                <th className="text-end py-3 px-3">{isArabic ? 'إجراء' : 'Action'}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const cfg = STATUS_CONFIG[it.status] || STATUS_CONFIG.expired;
                const { Icon } = cfg;
                const rem = it.status === 'active' ? remainingMinutes(it.expires_at) : null;
                return (
                  <tr key={it.id} className="border-t border-white/5" data-testid={`override-row-${it.code}`}>
                    <td className="py-3 px-3 font-mono font-bold tracking-wider">{it.code}</td>
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} text-[10px] font-bold`}>
                        <Icon className="w-3 h-3" />
                        {isArabic ? cfg.label_ar : cfg.label_en}
                      </span>
                    </td>
                    <td className="py-3 px-3">{it.created_by_name || '—'}</td>
                    <td className="py-3 px-3 text-xs text-muted-foreground">
                      {new Date(it.created_at).toLocaleString(isArabic ? 'ar-AE' : 'en-GB')}
                    </td>
                    <td className="py-3 px-3">
                      {it.status === 'active' ? (
                        <span className={rem < 5 ? 'text-red-400 font-bold' : 'text-emerald-400'}>
                          {rem} {isArabic ? 'د' : 'min'}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-3 px-3">
                      {it.used_by_name ? (
                        <div>
                          <div>{it.used_by_name}</div>
                          {it.used_for_membership && (
                            <div className="text-[10px] text-muted-foreground">#{it.used_for_membership}</div>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="py-3 px-3 text-xs text-muted-foreground">{it.note || '—'}</td>
                    <td className="py-3 px-3 text-end">
                      {it.status === 'active' && (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => handleCopy(it.code)} data-testid={`copy-${it.code}`}>
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300" onClick={() => handleRevoke(it.id, it.code)} data-testid={`revoke-${it.code}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default OverrideCodesTab;
